<template>
  <div class="mall-cart">
    <div class="page" :class="{ 'page--empty': isEmpty }">
      <MallHeader @back-click="goBack">
        <MallIconButton slot="right" :icon="documentIcon" :alt="'이용내역'" @click="goHistory" />
      </MallHeader>

      <div class="mall-cart__content">
        <div class="mall-cart__title">
          <span class="mall-cart__heading">{{ profile.name }}님의<br />장바구니</span>
          <span class="typo-body-s-medium">환자프로필마다 장바구니가 따로 관리돼요.</span>
        </div>
        <MallCartItemList
          v-if="isLoaded && !isEmpty"
          :items="displayItems"
          :selectedIds="selectedIds"
          @toggle-all="toggleAll"
          @toggle="toggleItem"
          @delete="deleteItem"
          @quantity-change="changeQuantity"
          @quantity-max="showMaxCountToast"
        />
      </div>

      <!-- 장바구니가 비었을 때 -->
      <template v-if="isEmpty">
        <div class="mall-cart__empty">
          <MallCartEmpty />
        </div>
        <MallBottomBar :buttons="emptyButtons" @click="goShop" />
      </template>

      <template v-else-if="isLoaded">
        <div class="mall-cart__thick-divider"></div>

        <MallCartOrderInfo
          :showDelegation="profile.isDelegated"
          :delegationApplied="delegationApplied"
          :prescriptionDays="prescriptionDays"
          :prescribedDays="null"
          :agreed="agreed"
          @delegation-change="onDelegationChange"
          @prescription-days-input="prescriptionDays = $event"
          @agree-change="agreed = $event"
        />

        <MallCartSummary
          :count="selectedItems.length"
          :payAmount="payAmount"
          :details="summaryDetails"
          :payDisabled="!canPay"
          @pay="pay"
        />
      </template>

      <!-- 위임청구 해제 확인 -->
      <MallConfirmPopup
        v-if="isDelegationOffPopup"
        :icon="warningIcon"
        :title="'일반 구매로 변경할까요?'"
        :messageLines="['위임청구를 해제하면 공단 지원금 없이', '전액 본인부담으로 결제돼요.']"
        :cancelLabel="'유지하기'"
        :confirmLabel="'일반 구매'"
        @close="isDelegationOffPopup = false"
        @cancel="isDelegationOffPopup = false"
        @confirm="turnOffDelegation"
      />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />
    </div>
  </div>
</template>

<script>
import Constant from '@/constant'
import convert from '@/utilities/convert.js'
import mall from '@/utilities/mall.js'
import MallIconButton from '@/components/new/atoms/MallIconButton.vue'
import MallHeader from '@/components/new/molecules/MallHeader.vue'
import MallConfirmPopup from '@/components/new/molecules/MallConfirmPopup.vue'
import MallBottomBar from '@/components/new/molecules/MallBottomBar.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallCartItemList from '@/components/new/organisms/MallCartItemList.vue'
import MallCartOrderInfo from '@/components/new/organisms/MallCartOrderInfo.vue'
import MallCartSummary from '@/components/new/organisms/MallCartSummary.vue'
import MallCartEmpty from '@/components/new/organisms/MallCartEmpty.vue'

export default {
  name: 'MallCart',
  components: {
    MallIconButton,
    MallHeader,
    MallConfirmPopup,
    MallBottomBar,
    MallToast,
    MallCartItemList,
    MallCartOrderInfo,
    MallCartSummary,
    MallCartEmpty,
  },
  data() {
    var profileId = Number(this.$route.query.profileId) || null
    var purchaseInfo = mall.getPurchaseInfo(profileId) || {}
    return {
      profileId: profileId,
      token: null,
      warningIcon: require('@/assets/mall/img_warning_triangle_3d.png'),
      documentIcon: require('@/assets/mall/icn_document_line.svg'),

      // isDelegated가 false면 위임청구 / 처방일수 영역이 노출되지 않음
      // TODO: 처방받은 일수는 API에 없어 미노출
      profile: { name: '', isDelegated: false },
      catalog: null,
      // 장바구니 상품 (mall.toProductView + quantity, soldOut: 판매 중인 상품 목록에 없는 상품)
      items: [],
      // 선택한 상품의 결제 예정금액 (POST /store/products/cost/)
      cost: null,
      costSeq: 0,
      isLoaded: false,
      isRequesting: false,
      toast: null,

      selectedIds: [],
      // 위임청구 적용 여부 (is_delegation_claim) - 해제하면(일반 구매) 서버가 전액 본인부담으로 계산
      // 결제 준비(checkout)에도 같은 값을 넘겨야 해서 처방전 → 구매하기 화면에 query(delegationClaim)로 전달
      delegationApplied: true,
      prescriptionDays: purchaseInfo.prescriptionDays ? String(purchaseInfo.prescriptionDays) : '',
      agreed: false,
      isDelegationOffPopup: false,
      emptyButtons: [
        { key: 'browse', label: '상품 둘러보기', type: 'primary' },
      ],
    }
  },
  computed: {
    isEmpty() {
      return this.isLoaded && this.items.length === 0
    },
    selectedItems() {
      return this.items.filter(item => !item.soldOut && this.selectedIds.indexOf(item.id) > -1)
    },
    purchaseInfo() {
      return Object.assign({}, mall.getPurchaseInfo(this.profileId), { prescriptionDays: this.prescriptionDays })
    },
    dateCount() {
      return mall.getDateCount(this.purchaseInfo)
    },
    // 위임청구 대상인데 적용을 해제한 경우 (전액 본인부담)
    isGeneralPurchase() {
      return this.profile.isDelegated && !this.delegationApplied
    },
    // 일반 구매면 보험적용가 대신 원가로 표시
    displayItems() {
      if (!this.isGeneralPurchase) return this.items
      return this.items.map(item => (item.originalPrice
        ? Object.assign({}, item, { price: item.originalPrice, originalPrice: undefined, discountRate: undefined })
        : item))
    },
    payAmount() {
      return this.cost ? this.cost.patient_cost : 0
    },
    summaryDetails() {
      if (!this.cost) return []
      // TODO: 배송비 API가 없어 배송비 항목 미노출
      var cost = this.cost
      // 위임청구 해제 / 미등록이면 공단 부담 0 (전액 본인부담)
      if (cost.insurance_cost === 0) {
        return [
          { label: '총 금액', value: this.formatPrice(cost.total_cost) },
        ]
      }
      return [
        { label: '총 금액', value: this.formatPrice(cost.total_cost) },
        { label: '공단 부담(' + (100 - cost.rate) + '%)', value: this.formatPrice(cost.insurance_cost) },
        { label: '본인 부담(' + cost.rate + '%)', value: this.formatPrice(cost.patient_cost) },
        {
          children: [
            { label: '남은 지원금', value: this.formatPrice(cost.remain_support_cost) },
            { label: '초과 본인부담금', value: this.formatPrice(cost.over_limit_cost) },
          ],
        },
        { label: '처방일수', value: (this.dateCount || 0) + '일' },
      ]
    },
    // 위임청구 적용 시 처방일수가 없으면 요양비 한도가 0으로 계산돼 처방일수 필수
    needsPrescriptionDays() {
      return this.profile.isDelegated && this.delegationApplied && !this.dateCount
    },
    canPay() {
      return this.agreed && this.selectedItems.length > 0 && this.cost !== null && !this.needsPrescriptionDays && !this.isRequesting
    },
  },
  watch: {
    prescriptionDays(value) {
      var digits = String(value).replace(/[^0-9]/g, '')
      if (digits !== value) {
        this.prescriptionDays = digits
        return
      }
      // 입력이 멈추면 구매정보에 저장하고 금액 다시 계산
      clearTimeout(this.daysTimer)
      this.daysTimer = setTimeout(() => {
        mall.setPurchaseInfo(this.profileId, this.purchaseInfo)
        this.calcCost()
      }, 400)
    },
  },
  mounted() {
    ChannelIO('shutdown')
    this.token = mall.getAccessToken()
    if (!this.token) {
      mall.goLogin(this.$router)
      return
    }
    // 프로필 목록 조회 실패는 오류 내용 없이 reject돼 상품 조회 오류(인증 등)로 처리
    Promise.all([
      mall.fetchProfile(this.$store, this.profileId, this.token).catch(() => null),
      mall.fetchCatalog(this.$store, this.profileId, this.token),
    ])
      .then((results) => {
        var profile = results[0]
        if (profile) {
          this.profile = {
            name: profile.profile.name,
            isDelegated: !!(profile.delegation_info && profile.delegation_info.is_delegated),
          }
        }
        this.catalog = results[1]
        return this.fetchCart(true)
      })
      .catch((err) => this.handleError(err))
  },
  beforeDestroy() {
    clearTimeout(this.daysTimer)
  },
  methods: {
    formatPrice(price) {
      return convert.setComma(price) + '원'
    },
    // 장바구니 조회 (처음엔 판매 중인 상품을 모두 선택)
    fetchCart(selectAll) {
      return this.$store.dispatch(Constant.FETCH_CART, {
        profile_id: this.profileId,
        date_count: this.dateCount,
        recipient_type_code: mall.getRecipientTypeCode(this.purchaseInfo),
        is_delegation_claim: this.delegationApplied,
        token: this.token,
      })
        .then((cart) => {
          this.items = ((cart && cart.items) || []).map(item => this.toCartItem(item))
          var selectable = this.items.filter(item => !item.soldOut).map(item => item.id)
          this.selectedIds = selectAll ? selectable : this.selectedIds.filter(id => selectable.indexOf(id) > -1)
          this.isLoaded = true
          return this.calcCost()
        })
    },
    toCartItem(cartItem) {
      var product = this.catalog.productMap[cartItem.product_id]
      if (!product) {
        return { id: cartItem.product_id, categoryLabel: '', name: '판매가 종료된 상품', image: mall.productImage(null), price: null, quantity: cartItem.quantity, soldOut: true, maxCount: null }
      }
      return Object.assign(mall.toProductView(product, this.catalog.categories), { quantity: cartItem.quantity, soldOut: false })
    },
    calcCost() {
      var seq = ++this.costSeq
      if (this.selectedItems.length === 0) {
        this.cost = null
        return Promise.resolve()
      }
      return this.$store.dispatch(Constant.CALC_STORE_COST, {
        cost: {
          profile_id: this.profileId,
          items: this.selectedItems.map(item => ({ product_id: item.id, quantity: item.quantity })),
          date_count: this.dateCount,
          recipient_type_code: mall.getRecipientTypeCode(this.purchaseInfo),
          is_delegation_claim: this.delegationApplied,
        },
        token: this.token,
      })
        .then((cost) => {
          // 늦게 온 이전 요청 결과는 무시
          if (seq === this.costSeq) this.cost = cost
        })
        .catch((err) => {
          if (seq === this.costSeq) this.cost = null
          this.handleError(err)
        })
    },
    // 전체 교체 API라 바뀐 목록 전체를 보냄
    // 판매종료 상품은 서버가 400으로 거절해 보내지 않음 (장바구니에서 빠짐)
    updateItems(items) {
      if (this.isRequesting) return Promise.resolve()
      this.isRequesting = true
      return this.$store.dispatch(Constant.UPDATE_CART_ITEMS, {
        profile_id: this.profileId,
        items: items.filter(item => !item.soldOut).map(item => ({ product_id: item.id, quantity: item.quantity })),
        token: this.token,
      })
        .then(() => this.fetchCart(false))
        .then(() => {
          this.isRequesting = false
        })
        .catch((err) => {
          this.isRequesting = false
          this.handleError(err)
          return Promise.reject(err)
        })
    },
    handleError(err) {
      if (mall.isAuthError(err)) {
        mall.goLogin(this.$router)
        return
      }
      this.toast = { key: Date.now(), title: '요청을 처리하지 못했어요.', message: mall.errorMessage(err) }
    },
    goBack() {
      mall.goBack(this.$router, this.$route, { path: '/mall/shop', query: { profileId: this.profileId } })
    },
    goShop() {
      this.$router.push({ path: '/mall/shop', query: { profileId: this.profileId } })
    },
    toggleAll() {
      var selectable = this.items.filter(item => !item.soldOut).map(item => item.id)
      var allSelected = selectable.length > 0 && selectable.every(id => this.selectedIds.indexOf(id) > -1)
      this.selectedIds = allSelected ? [] : selectable
      this.calcCost()
    },
    toggleItem(id) {
      var index = this.selectedIds.indexOf(id)
      if (index > -1) {
        this.selectedIds.splice(index, 1)
      } else {
        this.selectedIds.push(id)
      }
      this.calcCost()
    },
    deleteItem(id) {
      this.updateItems(this.items.filter(item => item.id !== id)).catch(() => {})
    },
    changeQuantity(id, quantity) {
      var target = this.items.find(item => item.id === id)
      if (!target) return
      // 상품별 최대 구매 수량을 넘지 않도록
      var next = target.maxCount ? Math.min(quantity, target.maxCount) : quantity
      if (next === target.quantity) return
      var items = this.items.map(item => (item.id === id ? Object.assign({}, item, { quantity: next }) : item))
      this.updateItems(items).catch(() => {})
    },
    showMaxCountToast(id, maxCount) {
      this.toast = { key: Date.now(), title: mall.maxCountMessage(maxCount) }
    },
    onDelegationChange(value) {
      if (value) {
        this.delegationApplied = true
        this.calcCost()
        return
      }
      // 해제는 확인 팝업 후 적용
      this.isDelegationOffPopup = true
    },
    turnOffDelegation() {
      this.delegationApplied = false
      this.isDelegationOffPopup = false
      this.calcCost()
    },
    pay() {
      if (!this.canPay) return
      mall.setPurchaseInfo(this.profileId, this.purchaseInfo)
      var goPrescription = () => {
        // 처방전 제출 화면을 거쳐 구매하기 화면으로 이동 (위임청구 해제는 query로 전달, 없으면 적용)
        this.$router.push({
          path: '/mall/prescription',
          query: { profileId: this.profileId, delegationClaim: this.delegationApplied ? undefined : 'false' },
        })
      }
      // 결제는 장바구니 전체로 진행돼 선택한 상품만 남김
      // TODO: 선택 해제한 상품은 장바구니에서 빠짐 - 일부 상품 결제 가능 여부 서버와 확인
      if (this.selectedItems.length === this.items.length) {
        goPrescription()
        return
      }
      this.updateItems(this.selectedItems).then(goPrescription).catch(() => {})
    },
    goHistory() {
      this.$router.push({ path: '/mall/history', query: { profileId: this.profileId } })
    },
  },
}
</script>

<style scoped>
  .mall-cart {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
    background: #F2F4F6;
  }
  /* 하단 고정 결제 영역(140px)에 마지막 항목이 가려지지 않도록 */
  .page {
    width: 100%;
    padding-bottom: 140px;
    background: #F2F4F6;
  }
  .mall-cart__content {
    display: flex;
    padding: 4px 16px 32px 16px;
    flex-direction: column;
    gap: 48px;
  }
  .mall-cart__title {
    display: flex;
    padding-left: 4px;
    flex-direction: column;
    gap: 4px;
  }
  .mall-cart__heading {
    color: var(--color-text-main);
    font-family: Pretendard;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-tight);
  }
  .mall-cart__thick-divider {
    height: 12px;
    background: #E5E8EB;
  }
  /* 빈 장바구니: 하단 버튼은 고정이라 아래 여백 없이 화면 높이를 채움 */
  .page--empty {
    display: flex;
    min-height: 100vh;
    padding-bottom: 0;
    flex-direction: column;
  }
  .page--empty .mall-cart__content {
    padding-bottom: 0;
  }
  /* 위쪽 헤더(64px) + 제목 영역(91px)만큼 아래 여백을 줘서 화면 세로 가운데에 오도록 (Figma 기준) */
  .mall-cart__empty {
    display: flex;
    padding: 0 16px 155px 16px;
    flex: 1;
    flex-direction: column;
    justify-content: center;
  }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallCart.vue